import { createContext, Deps, Ref, useEffect, useMemo, useRef } from "@lukekaalim/act";
import * as three from 'three';

export type Graphics3DSetup = {
  camera: Ref<three.PerspectiveCamera | null>,
  canvas: Ref<HTMLCanvasElement | null>,
  scene: Ref<three.Scene | null>,
}

export const graphicsSetupContext = createContext<Graphics3DSetup>({
  camera: new Ref(null),
  canvas: new Ref(null),
  scene: new Ref(null),
});

export type GraphicsEvents = {
  onFrame?: (now: DOMHighResTimeStamp) => void,
  onRender?: () => void,
  onResize?: (canvas: HTMLCanvasElement) => void
}

export const useGraphics3DSetup = (events: GraphicsEvents = {}, deps: Deps = []) => {
  const cameraRef = useRef<three.PerspectiveCamera | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<three.Scene | null>(null);

  const setup = useMemo(() => ({
    camera: cameraRef,
    canvas: canvasRef,
    scene: sceneRef
  }), [])

  useEffect(() => {
    const camera = cameraRef.get();
    const canvas = canvasRef.get();
    const scene = sceneRef.get();
    if (!camera || !canvas || !scene)
      return console.warn(`Failed to init due to missing: camera:${!!camera}, canvas:${!!canvas}, scene:${!!scene}`);

    const render = () => {
      if (events.onRender)
        events.onRender()
      renderer.render(scene, camera);
    };

    const onResize = () => {
      renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
      if (events.onResize)
        events.onResize(canvas)
      render();
    }

    const onFrame = (now: DOMHighResTimeStamp) => {
      if (events.onFrame)
        events.onFrame(now)

      render();

      frameId = requestAnimationFrame(onFrame);
    };
    
    let frameId = requestAnimationFrame(onFrame);

    const renderer = new three.WebGLRenderer({ canvas });
    const resizer = new ResizeObserver(onResize);
    
    resizer.observe(canvas);

    onResize();

    return () => {
      cancelAnimationFrame(frameId);
      resizer.disconnect();
    }
  }, deps)

  return setup;
};
